import React from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Buffer } from 'buffer';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { strings } from '../../resources/strings';
import { ShopLocation } from '../../models/ShopLocation';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { MainActions } from '../../actions/MainActions';
import { GenerateTransferredItemsSeparationListReportInput } from '../../services/Reports/GenerateTransferredItemsSeparationListReport/GenerateTransferredItemsSeparationListReportInput';
import { Button } from '../../common-components/Button/Button';
import { Field, FieldType } from '../../common-components/Field/Field';
import { If } from '../../common-components/If/If';
import { WaitingIcon } from '../../common-components/WaitingIcon/WaitingIcon';
import { FieldGroup, FieldLabel, Row } from './styles';

interface IModalTransferredItemsSeparationListReportProps {
  close: () => void;
}

const ReportAlert = () => (
  <Alert variant='warning'>
    <strong>Atenção!</strong> Como o relatório precisa verificar NFs transferidas a geração pode demorar demasiadamente.
  </Alert>
);

const ModalTransferredItemsSeparationListReport: React.FC<IModalTransferredItemsSeparationListReportProps> = ({
  close
}) => {

  const step = 50;

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session } = useAuth();

  const [initialDate, setInitialDate] = React.useState<Date | null>(new Date());
  const [finalDate, setFinalDate] = React.useState<Date | null>(new Date());
  const [locationId, setLocationId] = React.useState<number | null>(null);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [generating, setGenerating] = React.useState<boolean>(false);
  const [canceling, setCanceling] = React.useState<boolean>(false);
  const [percent, setPercent] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(true);

  const stringDataList = React.useRef<string[]>([]);
  const cancel = React.useRef(false);

  const {
    generateTransferredItemsSeparationListReportService,
    toastService,
    reportService,
    getLocationListService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    initForm();
    getLocations();
  }, [userLocations]);

  const memoBody = React.useMemo(() => {
    if (loading) {
      return (<WaitingIcon />);
    }

    return (
      <>
        <If condition={generating}>
          <FieldGroup marginBottom='10px'>
            <FieldLabel>Aguarde, gerando o relatório...</FieldLabel>
            <ProgressBar now={percent} label={`${percent}%`} variant='success' />
          </FieldGroup>
          <ReportAlert />
        </If>
        <If condition={!generating}>
          <FieldGroup>
            <FieldLabel>Período de abrangência do relatório</FieldLabel>
            <Row>
              <Field
                type={FieldType.DateTimePicker}
                value={initialDate}
                change={(value) => setInitialDate(value)}
                marginTop='0px'
                marginRight='5px'
              />
              <Field
                type={FieldType.DateTimePicker}
                value={finalDate}
                change={(value) => setFinalDate(value)}
                marginTop='0px'
                marginLeft='5px'
              />
            </Row>
          </FieldGroup>
          <Row>
            {!locationId || locationId === 1 
              ? (
                <span>Local inválido</span>
              ) : loadingLocations ? null : (
                <Field
                  type={FieldType.Select}
                  label={strings.Location}
                  selectProps={{
                    options: [...locations.filter((l) => l.id !== 1).filter(l => userLocations.some(ulid => ulid === l.id))],
                    displayField: 'name',
                    keyField: 'id'
                  }}
                  value={locationId}
                  change={(value) => setLocationId(value)}
                  marginTop={'0px'}
                  marginRight='5px'
                  disabled={userLocations.length <= 1}
                />
              )
            }
          </Row>
          <ReportAlert />
        </If>
      </>
    )
  }, [generating, percent, initialDate, finalDate, locationId, canceling, loading, loadingLocations, locations]);

  const memoFooter = React.useMemo(() => {
    return (
      <>
        <If condition={generating}>
          <Button text={strings.Cancel} disabled={!generating || canceling} click={() => cancelReport()} />
        </If>
        <If condition={!generating}>
          <Button text={strings.Confirm} click={() => confirmReport()} />
        </If>
      </>
    )
  }, [generating, percent, initialDate, finalDate, locationId, canceling, loading, loadingLocations, locations]);

  return (
    <Modal
      show={true}
      onHide={() => {
        if (generating || canceling) return;
        closeModal();
      }}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {strings.TransferredItemsSeparationListReport}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {memoBody}
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: 'center' }}>
        {memoFooter}
      </Modal.Footer>
    </Modal>
  );

  function closeModal() {
    mainActions.setNotWait(false);
    close();
  }

  function confirmReport() {
    setGenerating(true);
    setPercent(0);
    setCanceling(false);

    mainActions.setNotWait(true);

    stringDataList.current = [];
    cancel.current = false;

    const input = new GenerateTransferredItemsSeparationListReportInput();
    input.initialDate = moment(initialDate).format('YYYY-MM-DDTHH:mm:ss');
    input.finalDate = moment(finalDate).format('YYYY-MM-DDTHH:mm:ss');
    input.locationId = locationId;
    
    generateReport();
  }

  function generateReport() {
    if (cancel.current) {
      setGenerating(false);
      return;
    }

    setPercent(1);

    const input = new GenerateTransferredItemsSeparationListReportInput();
    input.initialDate = moment(initialDate).format('YYYY-MM-DDTHH:mm:ss');
    input.finalDate = moment(finalDate).format('YYYY-MM-DDTHH:mm:ss');
    input.locationId = locationId;
    input.includeHeaders = true;
    input.returnStringData = true;
    generateTransferredItemsSeparationListReportService.handleCsvOrders(input)
      .then(result => {
        try {
          stringDataList.current.push(result?.value?.stringData || '');
          setPercent(100);
          setTimeout(() => downloadReport(), 1000);
        } catch (error) {
          console.log('Error in generateTransferredItemsSeparationListReportService:', error);
          notifyProblemWhenGenerateReport();
        }
      })
      .catch(error => {
        console.log('Error in generateTransferredItemsSeparationListReportService:', error);
      });
  }

  function downloadReport() {
    const csvFile = stringDataList.current.map(value => Buffer.from(value, 'base64').toString()).join('');

    const reportName = 'Lista de Separação do estoque central' +
      (locationId ? ' da loja ' + locations.find(l => +l.id === +locationId)?.name : '') +   
      (initialDate ? ' a partir de ' + moment(initialDate).format('DD-MM-YYYY-HH-mm') : '') +
      (finalDate ? ' até ' + moment(finalDate).format('DD-MM-YYYY-HH-mm') : '');

    reportService.downloadCsvFromString(csvFile, `${reportName}.csv`);

    setGenerating(false);
    closeModal();
  }

  function notifyProblemWhenGenerateReport() {
    mainActions.setNotWait(false);
    toastService.warning('Não consegui gerar o relatório. Tente novamente mais tarde e se o problema persistir avise o time Tech.');
  }

  function cancelReport() {
    setCanceling(true);
    cancel.current = true;
    mainActions.setNotWait(false);
  }

  function initForm() {
    setLocationId(null)
    setLoading(true);
    setTimeout(() => {
      const userLocationId = userLocations.length > 0 && userLocations[0] === 1 && userLocations.length > 1
        ? userLocations[1]
        : null;
      setLocationId(userLocationId);
      setLoading(false);
    }, 1000);
  }

  function getLocations() {
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

}

export { ModalTransferredItemsSeparationListReport };